import { lazy, Suspense, forwardRef } from "react";
import HeroSection from "@/components/HeroSection";
import SEOHead from "@/components/SEOHead";
import JsonLd, { personSchema, websiteSchema, organizationSchema, breadcrumbSchema, aboutPageSchema } from "@/components/JsonLd";
import { useLanguage } from "@/i18n/LanguageContext";

// Lazy-load below-fold sections to reduce initial JS bundle (TBT optimization)
const AchievementsSection = lazy(() => import("@/components/AchievementsSection"));
const BlogSection = lazy(() => import("@/components/BlogSection"));
const LecturesSection = lazy(() => import("@/components/LecturesSection"));
const MediaSection = lazy(() => import("@/components/MediaSection"));
const PartnersSection = lazy(() => import("@/components/PartnersSection"));
const ZebbieSection = lazy(() => import("@/components/ZebbieSection"));
const CommunitySection = lazy(() => import("@/components/CommunitySection"));

const CANONICAL_ORIGIN = "https://tomasstrachota.cz";

const Index = forwardRef<HTMLDivElement>((_props, ref) => {
  const { locale } = useLanguage();
  // Always use the production canonical origin in JSON-LD so structured data
  // points to /cs even when served from a preview / mirror domain.
  const baseUrl = CANONICAL_ORIGIN;

  return (
    <div ref={ref} className="min-h-screen bg-background">
      <a href="#main-content" className="sr-only focus:not-sr-only focus:fixed focus:top-2 focus:left-2 focus:z-[100] focus:bg-primary focus:text-primary-foreground focus:px-4 focus:py-2 focus:rounded-sm focus:text-sm focus:font-semibold">
        {locale === "cs" ? "Přeskočit na obsah" : "Skip to content"}
      </a>
      <SEOHead
        titleCs="Tomáš Strachota — Chov exotických ptáků a zebřiček"
        titleEn="Tomáš Strachota — Exotic Bird & Zebra Finch Breeder"
        descriptionCs="Chovatel zebřiček a exotických ptáků, posuzovatel a cestovatel. 11+ let zkušeností, články, přednášky a poradna pro chovatele."
        descriptionEn="Zebra finch and exotic bird breeder, show judge and traveller. 11+ years of experience, articles, lectures and breeding tips."
        path="/"
      />
      <JsonLd data={organizationSchema(locale, baseUrl)} />
      <JsonLd data={personSchema(locale, baseUrl)} />
      <JsonLd data={websiteSchema(locale, baseUrl)} />
      <JsonLd data={breadcrumbSchema([
        { name: locale === "cs" ? "Domů" : "Home", url: `${baseUrl}/${locale}` },
      ])} />
      <JsonLd data={aboutPageSchema(locale, baseUrl)} />
      <main id="main-content">
        <HeroSection />
        {/* Per-section Suspense + dimensioned placeholders. Each section reserves its
            own vertical space so when one chunk arrives, only that section is
            replaced — sections below (and the footer) do NOT shift. Eliminates
            the previous 0.47 CLS where the footer jumped as chunks loaded. */}
        <Suspense fallback={<div aria-hidden="true" style={{ minHeight: "1100px" }} className="bg-background" />}>
          <AchievementsSection />
        </Suspense>
        <Suspense fallback={<div aria-hidden="true" style={{ minHeight: "1200px" }} className="bg-background" />}>
          <BlogSection />
        </Suspense>
        <Suspense fallback={<div aria-hidden="true" style={{ minHeight: "900px" }} className="bg-background" />}>
          <LecturesSection />
        </Suspense>
        <Suspense fallback={<div aria-hidden="true" style={{ minHeight: "800px" }} className="bg-background" />}>
          <MediaSection />
        </Suspense>
        <Suspense fallback={<div aria-hidden="true" style={{ minHeight: "700px" }} className="bg-background" />}>
          <PartnersSection />
        </Suspense>
        <Suspense fallback={<div aria-hidden="true" style={{ minHeight: "560px" }} className="bg-background" />}>
          <ZebbieSection />
        </Suspense>
        <Suspense fallback={<div aria-hidden="true" style={{ minHeight: "1100px" }} className="bg-background" />}>
          <CommunitySection />
        </Suspense>
      </main>
    </div>
  );
});

Index.displayName = "Index";

export default Index;
